import React, { useState } from 'react';
import api from '../services/api';
import Message from '../components/Message.jsx';
import UserPermissionsManager from '../components/UserPermissionsManager.jsx';
import HierarchicalPermissionsManager from '../components/HierarchicalPermissionsManager.jsx';
import RoleManager from '../components/RoleManager.jsx';
import UserRoleAssignment from '../components/UserRoleAssignment.jsx';
import PermissionGuard from '../components/PermissionGuard.jsx';
import { MODULES, ACTIONS } from '../context/PermissionsContext.jsx';
import CommonTable from '../components/common/CommonTable.jsx';

const UserManagement = () => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [message, setMessage] = useState({ text: '', type: '' });

const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.users.update(editingId, formData);
        setMessage({ text: 'User updated successfully!', type: 'success' });
      } else {
        await api.users.create(formData);
        setMessage({ text: 'User created successfully!', type: 'success' });
      }
      resetForm();
    } catch (error) {
      setMessage({ text: 'Failed to save user', type: 'error' });
    }
  };

  const handleEdit = (user) => {
    setFormData({
      username: user.username,
      password: ''
    });
    setEditingId(user.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this user?')) {
      try {
        await api.users.delete(id);
          setMessage({ text: 'User deleted successfully!', type: 'success' });
      } catch (error) {
        setMessage({ text: 'Failed to delete user', type: 'error' });
      }
    }
  };

  const resetForm = () => {
    setFormData({ username: '', password: '' });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <PermissionGuard module={MODULES.USER_MANAGEMENT} action={ACTIONS.VIEW} submodule={MODULES.USER_MANAGEMENT.submodules.USER_LIST}>
      <div className="">
      {message.text && (
        <Message 
          message={message.text} 
          type={message.type} 
          onClose={() => setMessage({ text: '', type: '' })} 
        />
      )}
      <div className="d-flex justify-content-between align-items-center mb-1">
        <h2 className='hd_a'>User Management</h2>
        <PermissionGuard module={MODULES.USER_MANAGEMENT} action={ACTIONS.CREATE} submodule={MODULES.USER_MANAGEMENT.submodules.USER_LIST}>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            <i className="bi bi-plus-lg me-1"></i> Add User
          </button>
        </PermissionGuard>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Users
          </button>
        </li>
        <li className="nav-item">
          <PermissionGuard module={MODULES.USER_MANAGEMENT} action={ACTIONS.VIEW} submodule={MODULES.USER_MANAGEMENT.submodules.ROLE_MANAGEMENT}>
            <button 
              className={`nav-link ${activeTab === 'roles' ? 'active' : ''}`}
              onClick={() => setActiveTab('roles')}
            >
              Role Management
            </button>
          </PermissionGuard>
        </li>
        <li className="nav-item">
          <PermissionGuard module={MODULES.USER_MANAGEMENT} action={ACTIONS.VIEW} submodule={MODULES.USER_MANAGEMENT.submodules.PERMISSIONS}>
            <button 
              className={`nav-link ${activeTab === 'assignments' ? 'active' : ''}`}
              onClick={() => setActiveTab('assignments')}
            >
              User Role Assignment
            </button>
          </PermissionGuard>
        </li>
      </ul>

      {activeTab === 'users' && (
        <>
          {showForm && (
            <div className="card mb-4">
              <div className="card-body px-0 pt-0">
                <h5>{editingId ? 'Edit User' : 'Add New User'}</h5>
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-5">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Username *"
                        value={formData.username}
                        onChange={(e) => setFormData({...formData, username: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-md-5">
                      <input
                        type="password"
                        className="form-control"
                        placeholder="Password *"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        required={!editingId}
                      />
                    </div>
                    <div className="col-md-2">
                      <button type="submit" className="btn btn-success me-2">Save</button>
                      <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancel</button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-body">
              <CommonTable
                dataSource={api.users.table}
                tableClassName="table"
                columns={[
                  { key: 'id', label: 'ID', sortable: true },
                  { key: 'username', label: 'Username', sortable: true },
                  { key: 'status', label: 'Status', sortable: true, render: (value, user) => <span className={`badge ${value ? 'bg-success' : 'bg-danger'}`}>{value ? 'Active' : 'Inactive'}</span> },
                  { key: 'createdAt', label: 'Created', sortable: true, render: (user) => user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-' }
                ]}
                renderActions={(user) => <>
                  <PermissionGuard module={MODULES.USER_MANAGEMENT} action={ACTIONS.EDIT} submodule={MODULES.USER_MANAGEMENT.submodules.USER_LIST}>
                    <button title='Edit' className="btn btn-sm btn-primary me-1" onClick={() => handleEdit(user)}>
                      <i className="bi bi-pencil-square"></i>
                    </button>
                  </PermissionGuard>
                  <PermissionGuard module={MODULES.USER_MANAGEMENT} action={ACTIONS.DELETE} submodule={MODULES.USER_MANAGEMENT.submodules.USER_LIST}>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(user.id)} style={{ fontSize: '13px', padding: '4px 10px' }}>
                      <i className="bi bi-trash-fill"></i>
                    </button>
                  </PermissionGuard>
                </>}
              />
            </div>
          </div>
        </>
      )}

      {activeTab === 'roles' && (
        <PermissionGuard module={MODULES.USER_MANAGEMENT} action={ACTIONS.VIEW} submodule={MODULES.USER_MANAGEMENT.submodules.ROLE_MANAGEMENT}>
          <HierarchicalPermissionsManager />
        </PermissionGuard>
      )}

      {activeTab === 'assignments' && (
        <PermissionGuard module={MODULES.USER_MANAGEMENT} action={ACTIONS.VIEW} submodule={MODULES.USER_MANAGEMENT.submodules.PERMISSIONS}>
          <UserRoleAssignment />
        </PermissionGuard>
      )}
      </div>
    </PermissionGuard>
  );
};

export default UserManagement;